import React from 'react';
import {useTranslation} from "react-i18next";
import useAfterUpdateEffect from "../../utils/useAfterUpdateEffect";
import apiPlatform from "../../apiPlatform";
import {has, omitBy} from "lodash";
import {toast} from "react-toastify";
import {FormFeedback, Input, InputGroup, InputGroupAddon} from "reactstrap";
import classnames from "classnames";
import Button from "../Button";
import TextWithTooltip from "../../componentsReusable/TextWithTooltip";
import PropTypes from "prop-types";
import moment from "moment/moment";

const VehicleVinInput = props => {
  const { t } = useTranslation('vehicle_vin_input');
  const [wasActive, setWasActive] = React.useState(false)
  const [fetchingVehicleData, setFetchingVehicleData] = React.useState(false)
  const [vehicleDataFetched, setVehicleDataFetched] = React.useState(false)
  const [vin, setVin] = React.useState(props.vin)
  const [validating, setValidating] = React.useState(false)
  const [validated, setValidated] = React.useState(false)
  const [probablyInvalid, setProbablyInvalid] = React.useState(false)
  const [vehicleDataReadyToFetch, setVehicleDataReadyToFetch] = React.useState(false)
  const [vinAlternatives, setVinAlternatives] = React.useState([])

  useAfterUpdateEffect(() => {
    if(wasActive){
      props.onChange(vin)
      setVehicleDataFetched(false)
      setProbablyInvalid(false)
      setValidated(false)
      setVinAlternatives([])
      setVehicleDataReadyToFetch(false)
      validateVin(vin)
    }
  }, [vin])

  useAfterUpdateEffect(() => {
    setVin(props.vin)
  }, [props.vin])

  const validateVin = (vin) => {
    if(vin.length === 0 || validated){
      return
    }

    if(vin.length !== 17){
      setProbablyInvalid(true)
      return
    }

    setValidating(true)
    apiPlatform.vehicles.checkVin({
        vin: vin
      })
      .then(res => {
        if(res.data?.alternatives) {
          const differences = res.data.alternatives.map(altVin => {
            return {
              alternativeVin: altVin,
              differences: compareVins(vin, altVin)
            };
          });

          setVinAlternatives(differences)
        }
        if(has(res, 'data.isValid')) {
          setProbablyInvalid(!res.data.isValid)
          setVehicleDataReadyToFetch(res.data.isValid)
        }
      })
      .finally(() => {
        setValidating(false)
        setValidated(true)
      })
  }

  const compareVins = (vin1, vin2) => {
    let differences = [];
    for (let i = 0; i < vin1.length; i++) {
      if (vin1[i] !== vin2[i]) {
        differences.push({
          position: i,
          original: vin1[i],
          alternative: vin2[i]
        });
      }
    }
    return differences;
  };

  const handleChangeVinClick = (vin) => {
    setVin(vin)
    setProbablyInvalid(false)
    setVinAlternatives([])
    validateVin(vin)
  };

  const fetchVehicleDataByVin = () => {
    setFetchingVehicleData(true)
    setVehicleDataFetched(true)

    apiPlatform.vehicles.fetchByVin(vin)
      .then(({data}) => {
        props.onFetchVehicleData(omitBy({
          carMake: data.carMake,
          carModel: data.carModel,
          brand: data.brand,
          model: data.model,
          capacity: data.capacity,
          engine: data.engine,
          enginePower: data.enginePower,
          enginePowerUnit: data.enginePowerUnit,
          productionDate: data.productionDate,
          firstRegistration: data.firstRegistration ? moment(data.firstRegistration).format('YYYY-MM-DD') : null,
          insuranceEnd: data.insuranceEnd ? moment(data.insuranceEnd).format('YYYY-MM-DD') : null,
          technicalInspectionEnd: data.technicalInspectionEnd ? moment(data.technicalInspectionEnd).format('YYYY-MM-DD') : null,
          registrationNumber: data.registrationNumber,
          vin: data.vin,
        }, v => !v))

        setVehicleDataReadyToFetch(true)
      })
      .catch(() => {
        setVehicleDataReadyToFetch(false)
        toast.error(t('fetch_data_error_msg'))
      })
      .finally(() => {
        setFetchingVehicleData(false)
      })
  }

  return <>
    <InputGroup>
      <Input
        className={classnames({
          'border-warning': probablyInvalid,
        })}
        invalid={!!props.errors}
        disabled={fetchingVehicleData}
        valid={vehicleDataReadyToFetch}
        onChange={e => setVin(e.target.value.replace(/[^0-9a-zA-Z\-]/g, '').toUpperCase().substr(0, 17))}
        onBlur={() => validateVin(vin)}
        onFocus={() => setWasActive(true)}
        placeholder={t('input_placeholder')}
        value={vin}
      />
      <InputGroupAddon addonType={'append'}>
        <Button
          loading={fetchingVehicleData || validating}
          disabled={probablyInvalid || vehicleDataFetched || vin?.length !== 17}
          onClick={fetchVehicleDataByVin}
          tooltip={t('btn_tooltip')}
        >
          <i className="fas fa-search" />
        </Button>
      </InputGroupAddon>
    </InputGroup>
    {(vehicleDataReadyToFetch && !fetchingVehicleData && !vehicleDataFetched) && <FormFeedback className="d-block text-success cursor-pointer" onClick={fetchVehicleDataByVin}>Mamy dane tego pojazdu, kliknij aby je pobrać</FormFeedback>}
    {(probablyInvalid || vinAlternatives?.length > 0) && <FormFeedback className="d-block text-warning">
      <span>{t('vin_structure_alert')}</span>
      {vinAlternatives.length > 0 && <span>{t('vin_structure_alert_alternatives')}</span>}
      {vinAlternatives.map((vinData, index) => (
        <TextWithTooltip
          key={index}
          onClick={() => handleChangeVinClick(vinData.alternativeVin)}
          tooltip={t('vin_structure_change_tooltip')}
          className="cursor-pointer text-secondary"
        >
          {vinData.alternativeVin.split('').map((char, i) => {
            const isDifferent = vinData.differences.some(diff => diff.position === i);
            return (
              <span
                key={i}
                className={classnames({
                  'text-success font-weight-bold': isDifferent,
                })}
              >{char}</span>
            );
          })}
        </TextWithTooltip>
      ))}
    </FormFeedback>}
    {props.errors && <FormFeedback className="d-block">{props.errors?.join('; ')}</FormFeedback>}
  </>;
};

VehicleVinInput.defaultProps = {
  vin: '',
}

VehicleVinInput.propTypes = {
  vin: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFetchVehicleData: PropTypes.func,
};

export default VehicleVinInput;